import React from 'react';
import { Heart } from 'lucide-react';
import SEO from '../../components/SEO';
import FAQRichSnippet from '../../components/FAQRichSnippet';

import Navigation from '../../components/Navigation';
import Footer from '../../components/Footer';

const PRP_FAQS = [
  { q: "What is PRP (Psychiatric Rehabilitation)?", a: "PRP is a community-based service that helps individuals with mental health conditions build skills for daily living, employment, social engagement, and independent functioning. Services are provided in-home and in the community." },
  { q: "Who qualifies for PRP services?", a: "PRP is available to Maryland residents with a qualifying mental health diagnosis who need support with daily living skills, social functioning, or community reintegration. Medicaid and most insurance plans cover PRP services." },
  { q: "What happens during a PRP session?", a: "Sessions focus on practical skill-building: managing symptoms, maintaining hygiene, cooking, budgeting, using transportation, job readiness, and building social connections. Your rehabilitation specialist works with you on personalized goals." },
  { q: "How long does someone typically receive PRP services?", a: "Duration varies based on individual needs and progress. Some clients benefit from PRP for several months, while others may participate for a year or more. Treatment plans are reviewed regularly." },
];

const PRPPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO title="PRP Services" description="SDORA Wellbeing's PRP helps adults and youth with serious mental illness develop independent living skills, community integration, employment support, and functional recovery in Pikesville & Baltimore County, MD. Maryland Medicaid accepted. What is PRP in Maryland? Learn more." path="/services/psychiatric-rehabilitation" />
      <Navigation />
      
      <div className="bg-gradient-to-br from-blue-50 to-purple-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-4">
            <Heart className="w-12 h-12 text-blue-600" />
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
              Psychiatric Rehabilitation Program (PRP)
            </h1>
          </div>
          <p className="text-xl text-gray-600 max-w-3xl">
            Evidence-based practices focused on empowering and restoring wellbeing and self-worth.
          </p>
        </div>
      </div>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Program Benefits</h2>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-blue-600 rounded-full mt-2"></span>
                  <div>
                    <h3 className="font-semibold text-lg">Skill Building</h3>
                    <p className="text-gray-600">Develop practical life skills for independence and success.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-blue-600 rounded-full mt-2"></span>
                  <div>
                    <h3 className="font-semibold text-lg">Community Support</h3>
                    <p className="text-gray-600">Connect with peers and build a supportive network.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-blue-600 rounded-full mt-2"></span>
                  <div>
                    <h3 className="font-semibold text-lg">Recovery Focus</h3>
                    <p className="text-gray-600">Emphasis on wellness and personal growth.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-blue-600 rounded-full mt-2"></span>
                  <div>
                    <h3 className="font-semibold text-lg">Long-term Success</h3>
                    <p className="text-gray-600">Tools and strategies for sustained wellbeing.</p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="bg-pink-50 p-8 rounded-xl">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">What We Offer</h2>
              <p className="text-gray-700 mb-4">Our PRP services include:</p>
              <ul className="space-y-2 text-gray-700">
                <li>• Life skills training</li>
                <li>• Social skills development</li>
                <li>• Coping strategies</li>
                <li>• Employment support</li>
                <li>• Independent living skills</li>
                <li>• Peer support groups</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <FAQRichSnippet faqs={PRP_FAQS} />
      </div>
      <Footer />
    </div>
  );
};

export default PRPPage;